// Registry i18n messages - English
// This file contains common messages shared across all registry components
// Module-specific messages (auth, logs, settings, user) are in their respective modules
// To use module translations, import and merge them in your project's i18n config
// Example: import { authEn } from '@/modules/auth/i18n'

export default {
  common: {
    welcome: 'Welcome',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    cancel: 'Cancel',
    save: 'Save',
    create: 'Create',
    delete: 'Delete',
    edit: 'Edit',
    preview: 'Preview',
    close: 'Close',
    confirm: 'Confirm',
    search: 'Search',
    refresh: 'Refresh',
    refreshed: 'Refreshed',
    filter: 'Filter',
    sort: 'Sort',
    actions: 'Actions',
    yes: 'Yes',
    no: 'No',
    previous: 'Previous',
    next: 'Next',
    columns: 'Columns',
    open_menu: 'Open menu',
    back: 'Back',
    done: 'Done',
    never: 'Never',
    or: 'or',
    created: 'Created',
    updated: 'Updated',
    toggleSidebar: 'Toggle sidebar',
    toggleDarkMode: 'Switch to {mode} mode',
    darkMode: 'dark',
    lightMode: 'light',
    toggleLanguage: 'Switch language to {locale}',
    copyToClipboard: {
      success: 'Copied to clipboard',
      copied: 'Copied',
      copy: 'Copy',
    },
    pagination: {
      totalRows: '{total} row(s) total',
      rowsPerPage: 'Rows per page',
      rowsPerPageValue: 'Rows per page: {count}',
      page: 'Page',
      of: 'of',
      goToFirstPage: 'Go to first page',
      goToPreviousPage: 'Go to previous page',
      goToNextPage: 'Go to next page',
      goToLastPage: 'Go to last page',
    },
    pages: {
      landing: 'Home',
      cookies: 'Cookies',
      privacy: 'Privacy Policy',
      terms: 'Terms of Use',
      contact: 'Contact',
      about: 'About',
      aiContext: 'AI Context',
      notFound: 'Page Not Found',
    },
  },
  markdown: {
    supported: 'Markdown is supported',
    supportedHint: 'You can use markdown to format your text. Supported features: headings, bold, italic, lists, links, images, code blocks, tables, quotes, and more.',
    showingPreview: 'Showing preview',
    linkSecurity: {
      linkTooLong: 'Link is too long and has been blocked',
      dangerousProtocolBlocked: 'Dangerous protocol has been blocked',
    },
  },
  validation: {
    required: 'This field is required',
    email: 'Invalid email address',
    min: 'Must be at least {min} characters',
    min_length: 'Must be at least {min} characters',
    max: 'Must be at most {max} characters',
    password_mismatch: 'Passwords do not match',
    password_too_short: 'Password must be at least {min} characters',
    invalid_token: 'Invalid or expired token',
  },
  errors: {
    generic: 'An error occurred. Please try again',
    validationError: 'Please check the form for errors',
    network: 'Network error. Please check your connection',
    unauthorized: 'You are not authorized to perform this action',
    not_found: 'Resource not found',
    server_error: 'Server error. Please try again later',
    chunkLoadError: {
      title: 'New Version Available',
      message: 'A new version of the application is available. The page needs to be reloaded to continue.',
      description: 'Please save your work before reloading.',
      reload: 'Reload Page',
      cancel: 'Cancel',
    },
  },
  navigation: {
    main: 'Navigation',
    dashboard: 'Dashboard',
    profile: 'Profile',
    settings: 'Settings',
    myGear: 'My Gear',
    public: 'Public',
  },
  footer: {
    cookies: 'Cookie Information',
    privacy: 'Privacy Policy',
    terms: 'Terms of Use',
    contact: 'Contact',
    github: 'GitHub',
    recaptcha: {
      notice: 'This site is protected by reCAPTCHA. The Google',
      privacy: 'Privacy Policy',
      and: ' and ',
      terms: 'Terms of Service',
      google: ' apply.',
    },
  },
  cookies: {
    title: 'Cookie Information',
    subtitle: 'Information about data usage in the application',
    whatAre: {
      title: 'What are cookies',
      description: 'Cookies are small text files that are stored on your device when browsing a website. They help the application remember your preferences and provide basic functionality.',
    },
    types: {
      title: 'Types of cookies we use',
      essential: {
        title: 'Essential cookies',
        description: 'These cookies are essential for the application to function and cannot be disabled. We use them for:',
        items: {
          session: 'Maintaining user session after login (JWT token)',
          security: 'Security purposes (protection against CSRF attacks)',
          preferences: 'Remembering basic preferences (language, dark mode)',
        },
      },
      functional: {
        title: 'Functional cookies',
        description: 'These cookies help the application function better and remember your choices:',
        items: {
          settings: 'Application settings stored locally (localStorage)',
          preferences: 'Display preferences and interface layout',
        },
      },
    },
    localStorage: {
      title: 'LocalStorage',
      description: 'In addition to cookies, the application also uses browser localStorage to store some data locally on your device, such as:',
      items: {
        settings: 'Application settings (language preferences, weight units)',
        cache: 'Temporary cache data to improve performance',
        offline: 'Offline data for operation without internet connection',
      },
    },
    management: {
      title: 'Cookie management',
      description: 'You can manage cookies in several ways:',
      items: {
        browser: 'Through browser settings - most browsers allow viewing and deleting cookies',
        block: 'Blocking certain types of cookies (may affect application functionality)',
        delete: 'Deleting cookies - you can delete them at any time, but this requires logging in again',
      },
      note: 'Note: Disabling essential cookies may cause some application features to stop working properly.',
    },
    thirdParty: {
      title: 'Third-party cookies',
      description: 'The application may use external services (e.g., Google Analytics, reCAPTCHA) that may place their own cookies. In accordance with our privacy policy, we inform about the use of such services and provide appropriate data protection.',
    },
    privacy: {
      title: 'Privacy',
      description: 'All cookies used by the application serve solely to provide service functionality. We do not use cookies to track users for marketing purposes or to share personal data with third parties without your consent.',
    },
    rodo: {
      title: 'GDPR',
      description: 'In accordance with the General Data Protection Regulation (GDPR), essential cookies do not require your consent as they are necessary for the service. For other cookies, we will ask for your consent if required.',
    },
  },
  privacy: {
    title: 'Privacy Policy',
    subtitle: 'How we protect your data',
    dataCollection: {
      title: 'Data Collection',
      description: 'Gear Stack application collects the following data:',
      items: {
        account: 'Account data: email, full name (required to create an account)',
        gear: 'Gear data: containers, items, weights, statuses (stored in database)',
        settings: 'Application settings: language preferences, weight units, other settings',
        usage: 'Usage data: access logs, actions taken in the application (for security and service improvement purposes)',
      },
    },
    dataStorage: {
      title: 'Data Storage',
      description: 'All data is stored securely on our servers in encrypted databases. We use standard security practices, including password hashing, connection encryption (HTTPS), and regular data backups.',
    },
    dataAccess: {
      title: 'Data Access',
      description: 'Your data is accessible only to you and is password-protected. Our administrators have technical access to data solely for maintenance and service improvement purposes, in accordance with security best practices.',
      thirdParties: 'We do not share your personal data with third parties, except as required by law or with your explicit consent.',
    },
    oauth: {
      title: 'OAuth Authentication',
      description: 'We offer the ability to log in using external OAuth providers (Google, Facebook). When using this feature:',
      items: {
        receive: 'We receive basic information from your account (email, name, profile picture)',
        store: 'We store only the necessary data to provide the service',
        share: 'We do not share your data back to OAuth providers without your consent',
        revoke: 'You can revoke access to your account at any time in your OAuth provider settings',
      },
      policies: 'By using OAuth, you also accept the privacy policy of the respective provider (Google or Facebook).',
    },
    dataDeletion: {
      title: 'Data Deletion',
      description: 'You have full control over your data and can delete it at any time. To delete your account and all associated data:',
      steps: {
        settings: 'Go to application settings',
        delete: 'Find the "Delete Account" section',
        confirm: 'Type "DELETE" in the confirmation field and confirm deletion',
      },
      alternative: 'Alternatively, you can contact us at',
      request: 'and request account deletion.',
      retention: 'After account deletion, your data is removed from our systems within 30 days. Some data may be retained longer if required by law or to resolve disputes.',
      oauth: 'If you use OAuth login, you must also revoke access to the application in your OAuth provider account settings (Google/Facebook).',
    },
    userRights: {
      title: 'Your Rights',
      description: 'In accordance with GDPR, you have the right to:',
      access: 'Access your personal data',
      rectification: 'Verify and correct your data',
      erasure: 'Delete your data ("right to be forgotten")',
      portability: 'Transfer your data to another service',
      object: 'Object to data processing',
      contact: 'To exercise any of these rights, please contact us at',
    },
    cookies: {
      title: 'Cookies',
      description: 'The application uses cookies to maintain user sessions and improve functionality. More information about cookie usage can be found on the',
      link: 'Cookie Information',
    },
    changes: {
      title: 'Policy Changes',
      description: 'In case of significant changes in the way data is processed, this page will be updated, and users will be notified of changes via email or notification in the application. Continued use of the application after changes means acceptance of the updated privacy policy.',
    },
    contact: {
      title: 'Contact',
      description: 'For questions about the privacy policy or data processing, please contact:',
    },
  },
  terms: {
    title: 'Terms of Use',
    subtitle: 'Terms and conditions for using the service',
    acceptance: {
      title: 'Acceptance of Terms',
      description: 'By using the Gear Stack application, you accept these terms of use. If you do not agree with any of the following terms, you should not use the service.',
    },
    account: {
      title: 'User Account',
      description: 'To use some features of the application, you must create an account. You agree to:',
      responsibilities: {
        provide: 'Provide true and current information during registration',
        maintain: 'Maintain the security of your account and password',
        notify: 'Immediately notify us of any unauthorized use of your account',
        responsible: 'Be responsible for all actions taken using your account',
      },
    },
    oauth: {
      title: 'OAuth Authentication',
      description: 'The application allows logging in using external OAuth providers (e.g., Google, Facebook). When using this feature:',
      rules: {
        provider: 'You accept the terms of use and privacy policy of the OAuth provider',
        data: 'You consent to sharing basic information from your account with us (email, name, profile picture)',
        revoke: 'You can revoke access to your account at any time in your OAuth provider settings',
      },
    },
    content: {
      title: 'User Content',
      description: 'The user retains full rights to content they create and store in the application. The user grants us only the necessary license to store, display, and process this data to provide the service.',
    },
    prohibited: {
      title: 'Prohibited Activities',
      description: 'The following are prohibited:',
      items: {
        illegal: 'Using the application for illegal purposes',
        harmful: 'Transmitting harmful, offensive, or illegal content',
        access: 'Attempting unauthorized access to the system or other user accounts',
        disrupt: 'Disrupting the operation of the application or servers',
        impersonate: 'Impersonating another person or pretending to be someone else',
      },
    },
    termination: {
      title: 'Account Termination',
      description: 'We reserve the right to suspend or delete your account in case of violation of these terms or for other important reasons. You can also delete your account at any time in the application settings.',
    },
    disclaimer: {
      title: 'Disclaimer',
      description: 'The application is provided "as is" without any warranties. We are not liable for any damages resulting from the use of the application.',
    },
    changes: {
      title: 'Changes to Terms',
      description: 'We reserve the right to make changes to these terms. Users will be notified of significant changes. Continued use of the application after changes means acceptance of the new terms.',
    },
    contact: {
      title: 'Contact',
      description: 'For questions about these terms, please contact us using the contact form available in the application.',
    },
  },
  contact: {
    title: 'Contact',
    subtitle: 'Get in touch with us',
    info: {
      title: 'Contact Information',
      description: 'If you have questions, suggestions, or want to report a problem, please contact us:',
    },
    email: {
      label: 'Email',
    },
    support: {
      title: 'Support',
      description: 'We try to respond to all messages within 7 days. If you are reporting a technical issue, please include detailed information about the problem.',
    },
  },
  landing: {
    title: 'Gear Stack',
    subtitle: 'Organize and manage your survival gear and bug-out bag equipment',
    welcomeBack: 'Welcome back, {name}!',
    feature1: {
      title: 'Organize',
      description: 'Keep track of all your gear in organized containers',
    },
    feature2: {
      title: 'Track',
      description: 'Monitor weight, readiness, and expiration dates',
    },
    feature3: {
      title: 'Prepare',
      description: 'Be ready for any situation with a well-prepared gear stack',
    },
    login: 'Log In',
    register: 'Sign Up',
    footer: 'Start organizing your gear today',
    localData: {
      title: 'You have containers in your browser',
      description: 'Log in or register to synchronize your data',
    },
    stats: {
      totalUsers: 'Total Users',
      totalContainers: 'Total Containers',
      totalItems: 'Total Items',
      newThisMonth: 'this month',
    },
  },
  notFound: {
    title: 'Page Not Found',
    description: 'The page you are looking for does not exist.',
    suggestions: 'Here are some helpful links:',
    goToContainers: 'Go to Containers',
    goToDashboard: 'Go to Dashboard',
    goToSettings: 'Go to Settings',
  },
  pwa: {
    update: {
      title: 'New Version Available',
      description: 'A new version of the application is available. Would you like to update now?',
      reload: 'Update Now',
      later: 'Later',
    },
  },
  fileUpload: {
    dropZone: {
      idle: 'Click to upload or drag and drop',
      dragging: 'Drop files here',
      disabled: 'Upload disabled',
    },
    formats: {
      image: 'JPEG, PNG, WEBP, GIF',
      any: 'Any file type',
    },
    limits: {
      maxSize: 'max {size} MB',
      maxFiles: '{count} files',
      maxFile: '{count} file',
    },
    errors: {
      tooManyFiles: 'Maximum {max} file(s) allowed',
      fileTooLarge: '{name}: File size exceeds {size} MB',
      invalidType: '{name}: Invalid file type',
    },
    imageGallery: {
      title: 'Images',
      noImages: 'No images uploaded yet',
      primary: 'Primary',
      tooltips: {
        setPrimary: 'Set as primary',
        dragToReorder: 'Drag to reorder',
        deleteImage: 'Delete image',
      },
      confirmDelete: 'Are you sure you want to delete this image?',
      messages: {
        loadFailed: 'Failed to load images',
        uploadSuccess: 'Image uploaded successfully',
        uploadSuccessMultiple: '{count} images uploaded successfully',
        uploadFailed: 'Failed to upload image',
        deleteSuccess: 'Image deleted successfully',
        deleteFailed: 'Failed to delete image',
        primarySuccess: 'Primary image updated',
        primaryFailed: 'Failed to set primary image',
        reorderSuccess: 'Images reordered',
        reorderFailed: 'Failed to reorder images',
      },
    },
  },
  about: {
    title: 'About Gear Stack',
    subtitle: 'A comprehensive web application for managing survival gear, bug-out bags, and outdoor equipment',
    tableOfContents: {
      title: 'Table of Contents',
    },
    overview: {
      title: 'Overview',
      description: 'Gear Stack is a full-stack application designed for outdoor enthusiasts, preppers, and survival gear collectors. It combines an intuitive front-end interface with a robust backend to provide secure multi-user gear management with cloud synchronization across devices.',
    },
    capabilities: {
      title: 'Key Capabilities',
      multiUser: 'Multi-User Platform - Secure user accounts with authentication and authorization',
      hybrid: 'Hybrid Architecture - Works offline with localStorage, syncs with cloud when online',
      organization: 'Advanced Organization - Hierarchical container system with nested items and weight tracking',
      metadata: 'Rich Metadata - Track weight, expiration dates, priorities, brands, and custom categories',
      portability: 'Data Portability - Import/export functionality with AI-ready markdown format',
    },
    coreFeatures: {
      title: 'Core Features',
      containers: {
        title: 'Container System',
        multiple: 'Multiple container types (Bug-out bags, EDC kits, get-home bags, medical kits, camping gear, and custom types)',
        hierarchical: 'Hierarchical organization - containers can contain other containers (nested packs, pouches in bags)',
        colors: 'Visual distinction - assign colors to containers for quick identification (10+ colors)',
        metadata: 'Container metadata - type, description, base weight, color coding',
        cycle: 'Cycle detection - prevents circular references in nested containers',
      },
      items: {
        title: 'Item Management',
        rich: 'Rich item data: name, quantity, weight (with unit selection: g, kg, oz, lb), category, priority, status (owned/missing/to buy), brand, notes, expiration date',
        categorization: 'Smart categorization - automatic category recognition based on item name (water, fire, food, shelter, first aid, tools, navigation, communication, clothing, hygiene, light, other)',
        status: 'Status tracking - mark items as owned, missing, or to buy',
        priority: 'Priority levels - low, medium, high, critical',
        expiration: 'Expiration tracking - monitor consumables and replace before they expire',
      },
      analytics: {
        title: 'Analytics & Insights',
        weight: 'Weight calculations - total pack weight with recursive calculation for nested containers, category-based weight distribution, base weight vs. consumables tracking',
        readiness: 'Readiness indicators - kit completeness percentage based on owned vs. missing items',
        charts: 'Donut charts - visual breakdown of weight or quantity by category',
        statistics: 'Item statistics - count items by status, category, or priority',
      },
      search: {
        title: 'Search & Filtering',
        smart: 'Smart search - find items by name, brand, or notes across all containers',
        filtering: 'Multi-criteria filtering - filter by category, status, priority, or container',
        sorting: 'Sorting options - sort by name, weight, expiration date, or priority',
        expired: 'Highlight expired items - visual warnings for expired or soon-to-expire items',
      },
      importExport: {
        title: 'Import/Export',
        json: 'JSON export/import - full data backup and restore',
        markdown: 'AI-ready markdown export - export containers to markdown format for AI processing with structured format, nested container support, and calculated weights',
        csv: 'CSV export - export data in CSV format for spreadsheet applications',
        crossDevice: 'Cross-device transfer - export from one device, import on another',
      },
    },
    businessFeatures: {
      title: 'Business Features',
      security: {
        title: 'User Management & Security',
        auth: 'User registration & login - email/password authentication with secure password hashing',
        oauth: 'OAuth social login - sign in with Google (GitHub support planned)',
        email: 'Email verification - confirm email addresses for account security',
        '2fa': 'Two-factor authentication (2FA) - TOTP (authenticator apps) and WebAuthn (passkeys/security keys)',
        password: 'Password management - reset forgotten passwords, change password for authenticated users',
        recaptcha: 'reCAPTCHA v3 protection - invisible bot protection on login, registration, and password reset',
        session: 'Session management - JWT tokens with automatic refresh, secure logout',
        deletion: 'Account deletion - GDPR-compliant soft delete with confirmation',
      },
      profile: {
        title: 'User Profile',
        management: 'Profile management - update name, email, and preferences',
        avatar: 'Avatar support - OAuth providers automatically provide profile pictures',
        settings: 'Preferred settings - weight units, language, theme preferences',
        security: 'Security settings - manage 2FA methods, view security status',
      },
      i18n: {
        title: 'Multi-Language Support',
        languages: 'English and Polish fully supported',
        detection: 'Automatic locale detection from browser',
        switching: 'Manual language switching in settings',
        localized: 'All UI text, validation messages, and emails localized',
      },
      theming: {
        title: 'Theming',
        dark: 'Dark mode - full dark theme support with system preference detection',
        persistence: 'Theme persistence - settings saved per user account',
      },
    },
    technical: {
      title: 'Technical Stack',
      frontend: {
        title: 'Frontend',
      },
      backend: {
        title: 'Backend',
      },
    },
  },
  aiContext: {
    title: 'AI Context',
    subtitle: 'Short description of Gear Stack in Markdown format for AI assistants like ChatGPT',
    card: {
      title: 'Copy Context to Clipboard',
      description: 'Click the button below to copy the context description. You can then paste it into ChatGPT or other AI assistants to provide context about Gear Stack.',
    },
    copied: 'Context copied to clipboard',
  },
  premium: {
    premiumFeature: 'Premium Feature',
    unlockPremiumFeatures: 'Unlock Premium Features - Upgrade to access AI Assistant and more',
  },
}
